import { Box, Input, HStack, Text } from "@chakra-ui/react";
import { memo, useEffect, useRef, useState } from "react";
import { theme } from "../../../theme/theme";

interface TestCreditCardWidgetProps {
  onChange?: (event: { error?: { message: string }; complete?: boolean }) => void;
  onReady?: (element: HTMLInputElement) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
}

/**
 * Test-mode credit card widget that uses regular HTML inputs instead of Stripe iframe.
 * This widget is designed for CI/testing environments to avoid Stripe iframe reliability issues.
 *
 * It provides the same interface as Stripe's CardElement but uses standard HTML inputs
 * that are much easier to interact with in automated tests.
 */
export const TestCreditCardWidget = memo(
  ({ onChange, onReady, onFocus, onBlur, disabled = false }: TestCreditCardWidgetProps) => {
    const [cardNumber, setCardNumber] = useState("");
    const [expiry, setExpiry] = useState("");
    const [cvc, setCvc] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [error, setError] = useState<string>();

    const cardNumberRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (cardNumberRef.current && onReady) {
        onReady(cardNumberRef.current);
      }
    }, [onReady]);

    const validateCardNumber = (value: string): string | undefined => {
      const cleaned = value.replace(/\s/g, "");
      if (cleaned.length === 0) return undefined;
      if (!/^\d+$/.test(cleaned)) return "Card number must contain only numbers";
      if (cleaned.length < 13) return "Card number is too short";
      if (cleaned.length > 19) return "Card number is too long";
      return undefined;
    };

    const validateExpiry = (value: string): string | undefined => {
      const cleaned = value.replace(/\s|\//g, "");
      if (cleaned.length === 0) return undefined;
      if (!/^\d+$/.test(cleaned)) return "Expiry must contain only numbers";
      if (cleaned.length < 4) return "Expiry is incomplete";

      const month = parseInt(cleaned.substring(0, 2));
      if (month < 1 || month > 12) return "Invalid expiry month";

      return undefined;
    };

    const validateCvc = (value: string): string | undefined => {
      if (value.length === 0) return undefined;
      if (!/^\d+$/.test(value)) return "CVC must contain only numbers";
      if (value.length < 3) return "CVC is incomplete";
      if (value.length > 4) return "CVC is too long";
      return undefined;
    };

    const isCardComplete = (
      nextCardNumber: string,
      nextExpiry: string,
      nextCvc: string,
      nextPostalCode: string,
    ): boolean => {
      const cardNumberValid =
        nextCardNumber.replace(/\s/g, "").length >= 13 &&
        !validateCardNumber(nextCardNumber);
      const expiryValid =
        nextExpiry.replace(/\s|\//g, "").length >= 4 && !validateExpiry(nextExpiry);
      const cvcValid = nextCvc.length >= 3 && !validateCvc(nextCvc);
      const postalCodeValid = nextPostalCode.trim().length > 0;
      return cardNumberValid && expiryValid && cvcValid && postalCodeValid;
    };

    const handleCardNumberChange = (value: string) => {
      setCardNumber(value);
      const error = validateCardNumber(value);
      setError(error);
      onChange?.({
        error: error ? { message: error } : undefined,
        complete: isCardComplete(value, expiry, cvc, postalCode),
      });
    };

    const handleExpiryChange = (value: string) => {
      // Auto-format expiry: add slash after 2 digits
      let formatted = value.replace(/\s|\//g, "");
      if (formatted.length > 2) {
        formatted = formatted.substring(0, 2) + " / " + formatted.substring(2, 4);
      }
      setExpiry(formatted);
      const error = validateExpiry(formatted);
      setError(error);
      onChange?.({
        error: error ? { message: error } : undefined,
        complete: isCardComplete(cardNumber, formatted, cvc, postalCode),
      });
    };

    const handleCvcChange = (value: string) => {
      setCvc(value);
      const error = validateCvc(value);
      setError(error);
      onChange?.({
        error: error ? { message: error } : undefined,
        complete: isCardComplete(cardNumber, expiry, value, postalCode),
      });
    };

    const handlePostalCodeChange = (value: string) => {
      setPostalCode(value);
      onChange?.({
        error: error ? { message: error } : undefined,
        complete: isCardComplete(cardNumber, expiry, cvc, value),
      });
    };

    const textColor = theme.colors.dune["600"];
    const errorColor = theme.colors.error["300"];

    const inputStyle = {
      fontSize: "14px",
      fontWeight: "500",
      fontFamily: "Roboto",
      color: error ? errorColor : textColor,
      border: "none",
      outline: "none",
      background: "transparent",
      padding: 0,
      height: "24px",
      _placeholder: {
        color: textColor,
      },
      _focus: {
        outline: "none",
        boxShadow: "none",
      },
    };

    return (
      <Box width="100%" height="100%">
        <HStack spacing="8px" width="100%" height="100%">
          <Input
            ref={cardNumberRef}
            data-elements-stable-field-name="cardNumber"
            placeholder="1234 1234 1234 1234"
            value={cardNumber}
            onChange={(e) => handleCardNumberChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={disabled}
            maxLength={19}
            sx={inputStyle}
            flex={2}
          />
          <Text
            fontSize="10px"
            fontFamily="Roboto"
            color="transparent.white.40"
            whiteSpace="nowrap"
            fontWeight="500"
          >
            TEST WIDGET
          </Text>
        <Box width="1px" height="16px" bg="transparent.white.10" />
        <Input
          data-elements-stable-field-name="cardExpiry"
          placeholder="MM / YY"
          value={expiry}
          onChange={(e) => handleExpiryChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          maxLength={7}
          sx={inputStyle}
          flex={1}
        />
        <Box width="1px" height="16px" bg="transparent.white.10" />
        <Input
          data-elements-stable-field-name="cardCvc"
          placeholder="CVC"
          value={cvc}
          onChange={(e) => handleCvcChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          maxLength={4}
          sx={inputStyle}
          flex={1}
        />
        <Box width="1px" height="16px" bg="transparent.white.10" />
        <Input
          data-elements-stable-field-name="postalCode"
          placeholder="ZIP"
          value={postalCode}
          onChange={(e) => handlePostalCodeChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          maxLength={10}
          sx={inputStyle}
          flex={1}
        />
        </HStack>
      </Box>
    );
  }
);

TestCreditCardWidget.displayName = "TestCreditCardWidget";
